import React, { useEffect, useState } from "react";
import {
  Download,
  FileText,
  Loader2,
  Maximize2,
} from "lucide-react";

import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { api } from "@/lib/pp/api";

/**
 * Static, non-scrolling invoice preview (matches the pharmacy handoff PDF
 * preview): one page image at a time with pill-style page-toggle buttons
 * and a download row that uses a Blob (not <a href>) so iOS Safari never
 * navigates the current tab away from the CRM.
 */
export default function InvoicePreviewDialog({ open, record, onOpenChange }) {
  const [loading, setLoading] = useState(false);
  const [pages, setPages] = useState([]);
  const [filename, setFilename] = useState("");
  const [page, setPage] = useState(1);
  const [error, setError] = useState("");
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (!open || !record?.id) return;
    let cancelled = false;
    setLoading(true);
    setError("");
    setPages([]);
    setPage(1);
    api
      .get(`/records/${record.id}/invoice-preview`)
      .then(({ data }) => {
        if (cancelled) return;
        setPages(data.page_images || []);
        setFilename(data.filename || "invoice.pdf");
      })
      .catch(() => !cancelled && setError("Preview failed"))
      .finally(() => !cancelled && setLoading(false));
    return () => {
      cancelled = true;
    };
  }, [open, record?.id]);

  const total = pages.length || 1;
  const currentImg = pages[page - 1];

  // Fetch PDF as a Blob and trigger download (or open in new tab for view).
  // Using blob URLs guarantees the current tab NEVER navigates away.
  const fetchPdfBlob = async () => {
    const res = await api.get(`/records/${record.id}/invoice`, {
      responseType: "blob",
    });
    return res.data;
  };

  const handleDownload = async () => {
    if (!record?.id || busy) return;
    setBusy(true);
    try {
      const blob = await fetchPdfBlob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = filename || "invoice.pdf";
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      setTimeout(() => URL.revokeObjectURL(url), 4000);
    } catch (e) {
      setError("Download failed");
    } finally {
      setBusy(false);
    }
  };

  const handleOpenFullscreen = async () => {
    if (!record?.id || busy) return;
    setBusy(true);
    try {
      const blob = await fetchPdfBlob();
      const url = URL.createObjectURL(blob);
      // Opening a blob: URL in a new tab keeps CRM tab intact regardless of iOS behavior.
      window.open(url, "_blank", "noopener,noreferrer");
      setTimeout(() => URL.revokeObjectURL(url), 60000);
    } catch (e) {
      setError("Could not open preview");
    } finally {
      setBusy(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent
        className="max-w-4xl max-h-[92vh] p-0 overflow-hidden bg-white flex flex-col"
        data-testid="pp-invoice-preview-dialog"
      >
        <DialogHeader className="px-5 pt-5 pb-4 pr-10 border-b border-slate-100 space-y-0">
          <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-3">
            <div className="text-left">
              <DialogTitle
                style={{ fontFamily: "'Work Sans', system-ui, sans-serif" }}
                className="text-[#1C2B26] text-base"
              >
                Invoice preview
              </DialogTitle>
              {(record?.patient_name || record?.medication) && (
                <p className="text-xs text-slate-500 mt-1">
                  {[record?.medication, record?.patient_name].filter(Boolean).join(" · ")}
                  {filename ? ` · ${filename}` : ""}
                </p>
              )}
            </div>
            <button
              type="button"
              onClick={handleOpenFullscreen}
              disabled={busy || !record?.id}
              data-testid="pp-invoice-open-tab"
              className="self-start inline-flex items-center gap-1.5 rounded-full border border-[#2D5A4C] text-[#2D5A4C] hover:bg-[#2D5A4C]/10 text-xs font-semibold px-3 py-1.5 transition whitespace-nowrap disabled:opacity-50"
            >
              <Maximize2 className="h-3.5 w-3.5" />
              Full screen
            </button>
          </div>
        </DialogHeader>

        <div className="bg-slate-50 p-4 flex-1 overflow-y-auto min-h-0">
          <div className="border border-slate-200 rounded-xl bg-white overflow-hidden flex items-center justify-center max-h-[58vh]">
            {loading ? (
              <div className="flex flex-col items-center gap-2 text-sm text-slate-500 py-16">
                <Loader2 className="h-6 w-6 animate-spin text-[#2D5A4C]" />
                Rendering preview…
              </div>
            ) : error ? (
              <div className="flex flex-col items-center gap-2 text-sm text-slate-500 py-16">
                <FileText className="h-6 w-6 text-slate-300" />
                {error}
              </div>
            ) : currentImg ? (
              <img
                src={currentImg}
                alt={`Invoice page ${page}`}
                data-testid="pp-invoice-preview-image"
                className="block max-h-[58vh] w-auto max-w-full object-contain"
              />
            ) : (
              <div className="flex flex-col items-center gap-2 text-sm text-slate-500 py-16">
                <FileText className="h-6 w-6 text-slate-300" />
                No preview available
              </div>
            )}
          </div>

          {/* Segmented pill page toggle (only if >1 page) */}
          {total > 1 && (
            <div className="mt-4 flex items-center justify-center">
              <div
                role="tablist"
                aria-label="Invoice pages"
                className="inline-flex items-center bg-white border border-slate-200 rounded-full p-1 shadow-sm"
                data-testid="pp-invoice-page-tabs"
              >
                {pages.map((_, i) => {
                  const n = i + 1;
                  const active = n === page;
                  return (
                    <button
                      key={n}
                      type="button"
                      role="tab"
                      aria-selected={active}
                      onClick={() => setPage(n)}
                      data-testid={`pp-invoice-page-${n}`}
                      className={
                        "min-w-[86px] px-4 py-1.5 rounded-full text-xs font-semibold transition-colors " +
                        (active
                          ? "bg-[#0F3B2E] text-white shadow-sm"
                          : "text-[#1C2B26] hover:bg-slate-100")
                      }
                    >
                      Page {n}
                    </button>
                  );
                })}
              </div>
            </div>
          )}

          {/* Download row */}
          <div className="mt-4 flex flex-wrap items-center justify-center gap-2">
            <button
              type="button"
              onClick={handleDownload}
              disabled={busy || !record?.id}
              data-testid="pp-invoice-download-current"
              className="inline-flex items-center gap-1.5 rounded-full bg-white border border-slate-200 hover:border-[#2D5A4C] hover:text-[#2D5A4C] text-[#1C2B26] font-semibold text-xs px-4 py-1.5 transition disabled:opacity-50 disabled:cursor-wait"
            >
              {busy ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Download className="h-3.5 w-3.5" />}
              Download this page{total > 1 ? ` (Page ${page})` : ""}
            </button>
            <button
              type="button"
              onClick={handleDownload}
              disabled={busy || !record?.id}
              data-testid="pp-invoice-download-all"
              className="inline-flex items-center gap-1.5 rounded-full bg-[#0F3B2E] hover:bg-[#0A2A20] text-white font-semibold text-xs px-4 py-1.5 transition disabled:opacity-50 disabled:cursor-wait"
            >
              {busy ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Download className="h-3.5 w-3.5" />}
              {total > 1 ? "Download full PDF" : "Download PDF"}
            </button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}
